/* eslint-disable no-bitwise */
import { Button, Typography } from "@mui/material";
import { Map } from "ol";
import React from "react";
import { useTranslation } from "react-i18next";
import { generateFleetItemId } from "~/pages/fleets-page/generateFleedItemId";
import { setSelectedFleetItemId } from "~/redux/reducers/fleetReducer";
import { useAppDispatch } from "~/redux/store/store";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";
import { vesselTypeToTranslationKey } from "../functions/vesselTypeToTranslationKey";
import { VesselMapItem } from "../types/mapItems";
import { GenericPopup } from "./GenericPopup";
import { VesselContactForm } from "./VesselContactForm";

type Props = {
  map: Map;
} & (ClickProps | HoverProps) &
  FleetProps;

type ClickProps = {
  clickData: VesselMapItem;
  hoverData?: VesselMapItem;
};

type HoverProps = {
  hoverData: VesselMapItem;
  clickData?: VesselMapItem;
};

type FleetProps = {
  fleet?: boolean;
};

// Parse timestamp
export const parseETA = (eta: number): Date => {
  const minute = eta & 0b111111;
  const hour = (eta >>> 6) & 0b11111;
  const day = (eta >>> 11) & 0b11111;
  const month = (eta >>> 16) & 0b11111;

  const year = new Date().getUTCFullYear();

  return new Date(Date.UTC(year, month - 1, day, hour, minute));
};

export const VesselPopup = ({
  hoverData,
  clickData,
  map,
  fleet,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("vessels");
  const dateFormatter = useDateFormatter();
  const dispatch = useAppDispatch();

  const data = clickData ?? hoverData;

  if (!data) return null;

  const {
    timestampExternal,
    name,
    eta,
    destination,
    callSign,
    shipType,
    heading,
    sog,
    mmsi,
    referencePointA,
    referencePointB,
    referencePointC,
    referencePointD,
    contactInfos,
  } = data;

  return (
    <GenericPopup
      map={map}
      avatarText={callSign ?? ""}
      title={`${t("vessel")} - ${name ?? ""}`}
      subheader={dateFormatter({ date: new Date(timestampExternal) })}
      clicked={!!clickData}
      content={
        <>
          <Typography variant="body2" align="left" noWrap>
            {`${t("mmsi")}: ${mmsi}`}
          </Typography>
          <Typography variant="body2" align="left" noWrap>
            {`${t("destination")}: ${destination}`}
          </Typography>
          <Typography variant="body2" align="left" noWrap>
            {`ETA: ${eta ? dateFormatter({ date: parseETA(eta) }) : "-"}`}
          </Typography>
          <Typography variant="body2" align="left" noWrap>
            {`${t("type")}: ${t(vesselTypeToTranslationKey(shipType))}`}
          </Typography>
          <Typography variant="body2" align="left" noWrap>
            {`${t("length")}: ${referencePointA + referencePointB ?? "-"} m`}
          </Typography>
          <Typography variant="body2" align="left" noWrap>
            {`${t("width")}: ${referencePointC + referencePointD ?? "-"} m`}
          </Typography>
          <Typography variant="body2" align="left" noWrap>
            {`${t("heading")}: ${heading !== 511 ? heading : "-"} °`}
          </Typography>
          <Typography variant="body2" align="left" noWrap>
            {`${t("speed")}: ${sog} ${t("knots")}`}
          </Typography>
          <VesselContactForm mmsi={mmsi} contactInfos={contactInfos} />
          {fleet && (
            <Button
              style={{ marginLeft: "auto", display: "flex" }}
              variant="contained"
              color="secondary"
              onClick={() => {
                dispatch(
                  setSelectedFleetItemId(
                    generateFleetItemId({
                      type: "ais",
                      data,
                      index: 0,
                      id: "",
                    }),
                  ),
                );
                const ol = map?.getOverlayById("popup");
                ol?.setPosition(undefined);
                ol?.set("clickedOpen", false);
              }}
            >
              {t("fleets:select-item")}
            </Button>
          )}
        </>
      }
    />
  );
};
